import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Pencil, Trash, User } from 'lucide-react';
import UserPortalFormPanel from './UserPortalFormPanel';

const TIPO_USUARIO_LABEL = {
  comum: 'Comum',
  administrador: 'Administrador',
};

function normalizeText(v) {
  if (v == null) return '';
  return String(v)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

async function apiVc(path, { method = 'GET', body } = {}) {
  const token = localStorage.getItem('authToken');
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
  };
  const res = await fetch(`/api/vc-usr${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    throw new Error(json.message || `Erro ${res.status}`);
  }
  return json;
}

function CustomerUserAvatar({ photoUrl }) {
  const [failed, setFailed] = useState(false);
  const showPhoto = Boolean(photoUrl) && !failed;

  useEffect(() => {
    setFailed(false);
  }, [photoUrl]);

  return (
    <span className="uc-mu-avatar uc-mu-avatar--embedded" aria-hidden="true">
      {showPhoto ? (
        <img
          className="uc-mu-avatar-img"
          src={photoUrl}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      ) : (
        <User size={14} strokeWidth={2} />
      )}
    </span>
  );
}

export default function CustomerUsersPanel({ scope, onListagemRefresh, embedded = false }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [userSidePanel, setUserSidePanel] = useState(null);
  const [userSidePanelEntered, setUserSidePanelEntered] = useState(false);

  const loadUsers = useCallback(async () => {
    if (!scope?.idGrupoRef || !scope?.codigosCliente?.length) {
      setUsers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const q = encodeURIComponent(scope.codigosCliente.join(','));
      const j = await apiVc(
        `/portal-usuarios?idGrupoRef=${encodeURIComponent(scope.idGrupoRef)}&codigos=${q}`
      );
      setUsers(j.data || []);
    } catch (e) {
      setUsers([]);
      window.alert(e.message || 'Erro ao listar usuários');
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!userSidePanel) {
      setUserSidePanelEntered(false);
      return undefined;
    }
    const id = requestAnimationFrame(() => setUserSidePanelEntered(true));
    return () => cancelAnimationFrame(id);
  }, [userSidePanel]);

  useEffect(() => {
    if (!scope) {
      setQuery('');
      setUserSidePanel(null);
      setUserSidePanelEntered(false);
    }
  }, [scope]);

  const filteredUsers = useMemo(() => {
    const q = normalizeText(query);
    if (!q) return users;
    return users.filter((u) =>
      normalizeText(
        [
          u.nome,
          u.email,
          u.cargo,
          TIPO_USUARIO_LABEL[u.tipoUsuario] || u.tipoUsuario,
          String(u.enderecos),
        ].join(' ')
      ).includes(q)
    );
  }, [users, query]);

  const closeUserPanel = useCallback(() => {
    setUserSidePanelEntered(false);
    window.setTimeout(() => setUserSidePanel(null), 280);
  }, []);

  const handleSaveUserPortal = useCallback(
    async (payload) => {
      if (!scope) return;
      const { idGrupoRef, codigosCliente } = scope;
      const contratoLocais = payload.contratoLocais || [];
      try {
        if (payload.mode === 'edit' && payload.userId) {
          await apiVc(`/portal-usuarios/${payload.userId}`, {
            method: 'PUT',
            body: {
              idGrupoRef,
              codigos: codigosCliente,
              nome: payload.nome,
              email: payload.email,
              cargo: payload.cargo,
              foneWhatsapp: payload.foneWhatsapp,
              foneFixo: payload.foneFixo,
              isActive: payload.isActive !== false,
              tipoUsuario: payload.tipoUsuario,
              senha: payload.senha || '',
              permissoes: payload.permissoes,
              contratoLocais,
            },
          });
        } else {
          await apiVc('/portal-usuarios', {
            method: 'POST',
            body: {
              idGrupoRef,
              codigos: codigosCliente,
              nome: payload.nome,
              email: payload.email,
              cargo: payload.cargo,
              foneWhatsapp: payload.foneWhatsapp,
              foneFixo: payload.foneFixo,
              isActive: payload.isActive !== false,
              tipoUsuario: payload.tipoUsuario,
              senha: payload.senha,
              permissoes: payload.permissoes,
              contratoLocais,
            },
          });
        }
        await loadUsers();
        if (onListagemRefresh) await onListagemRefresh();
        closeUserPanel();
      } catch (e) {
        throw e instanceof Error ? e : new Error(String(e?.message || e || 'Erro ao salvar'));
      }
    },
    [scope, closeUserPanel, loadUsers, onListagemRefresh]
  );

  const requestDeleteUser = useCallback(
    async (user) => {
      if (!scope) return;
      if (!window.confirm(`Excluir o usuário "${user.nome}"?`)) return;
      const { idGrupoRef, codigosCliente } = scope;
      try {
        const q = encodeURIComponent(codigosCliente.join(','));
        await apiVc(
          `/portal-usuarios/${user.id}?idGrupoRef=${encodeURIComponent(idGrupoRef)}&codigos=${q}`,
          { method: 'DELETE' }
        );
        await loadUsers();
        if (onListagemRefresh) await onListagemRefresh();
      } catch (e) {
        window.alert(e.message || 'Erro ao excluir');
      }
    },
    [scope, loadUsers, onListagemRefresh]
  );

  const panelClass = embedded ? 'uc-users-panel-embedded' : 'uc-users-panel';

  return (
    <div className={panelClass}>
      <div className="uc-manage-users-toolbar uc-manage-users-toolbar--embedded">
        <div className="uc-manage-users-filter uc-manage-users-filter--embedded">
          <input
            className="uc-manage-users-search uc-cg-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filtrar (nome, e-mail, cargo, tipo…)"
            aria-label="Filtrar usuários do portal"
            disabled={loading}
          />
        </div>
        <button
          type="button"
          className="users-customers-add-btn uc-manage-users-add-btn"
          onClick={() => setUserSidePanel({ mode: 'new' })}
          disabled={loading}
        >
          Incluir usuário
        </button>
      </div>

      <div
        className="uc-manage-users-table-wrap uc-manage-users-table-wrap--embedded"
        role="region"
        aria-label="Usuários vinculados"
      >
        {loading ? (
          <div className="uc-users-panel-loading">
            <Loader2 className="users-customers-modal-spinner" size={20} />
            <span>Carregando usuários…</span>
          </div>
        ) : (
          <table className="uc-manage-users-table uc-manage-users-table--embedded">
            <thead>
              <tr>
                <th className="uc-mu-col-av uc-mu-col-av--embedded" aria-label="Foto do usuário" />
                <th className="uc-mu-col-nome">Nome</th>
                <th className="uc-mu-col-email">E-mail</th>
                <th className="uc-mu-col-cargo">Cargo</th>
                <th className="uc-mu-col-tipo">Tipo</th>
                <th className="uc-mu-col-act" aria-label="Ações" />
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td className="users-customers-empty" colSpan={6}>
                    {users.length === 0
                      ? 'Nenhum usuário vinculado a este cliente.'
                      : 'Nenhum usuário encontrado com este filtro.'}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u) => (
                  <tr key={u.id} className="users-customers-row uc-manage-users-row">
                    <td className="uc-mu-avatar-cell">
                      <CustomerUserAvatar photoUrl={u.profilePhotoUrl} />
                    </td>
                    <td className="uc-cell-truncate" title={u.nome}>
                      {u.nome}
                    </td>
                    <td className="uc-cell-truncate uc-cell-mono" title={u.email}>
                      {u.email}
                    </td>
                    <td className="uc-cell-truncate" title={u.cargo}>
                      {u.cargo}
                    </td>
                    <td className="uc-mu-tipo-cell">
                      <span
                        className={
                          u.tipoUsuario === 'administrador'
                            ? 'uc-mu-tipo uc-mu-tipo--admin'
                            : 'uc-mu-tipo uc-mu-tipo--comum'
                        }
                      >
                        {TIPO_USUARIO_LABEL[u.tipoUsuario] || TIPO_USUARIO_LABEL.comum}
                      </span>
                    </td>
                    <td className="uc-mu-actions">
                      <div className="uc-action-btns uc-manage-users-actions">
                        <button
                          type="button"
                          className="users-customers-edit-grupo-btn"
                          onClick={() => setUserSidePanel({ mode: 'edit', user: u })}
                          aria-label={`Editar ${u.nome}`}
                        >
                          <Pencil size={13} strokeWidth={2} />
                        </button>
                        <button
                          type="button"
                          className="users-customers-delete-btn"
                          onClick={() => requestDeleteUser(u)}
                          aria-label={`Excluir ${u.nome}`}
                        >
                          <Trash size={13} strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {userSidePanel &&
        createPortal(
          <div
            className={[
              'uc-user-side-overlay',
              'uc-user-side-overlay--portal',
              userSidePanelEntered ? 'uc-user-side-overlay--visible' : '',
            ].join(' ')}
            role="presentation"
          >
            <button
              type="button"
              className="uc-user-side-dim"
              onClick={closeUserPanel}
              tabIndex={-1}
              aria-label="Fechar painel lateral"
            />
            <aside
              className={[
                'uc-user-side-panel',
                userSidePanelEntered ? 'uc-user-side-panel--open' : '',
              ].join(' ')}
              aria-label="Cadastro de usuário"
            >
              <div className="uc-user-side-head">
                <div className="uc-user-side-head-title">
                  {userSidePanel.mode === 'edit' ? userSidePanel.user.nome : 'Novo usuário'}
                </div>
                <button
                  type="button"
                  className="uc-user-side-close"
                  onClick={closeUserPanel}
                  aria-label="Fechar"
                >
                  ×
                </button>
              </div>
              <div className="uc-user-side-body">
                <UserPortalFormPanel
                  key={`${userSidePanel.mode}-${userSidePanel.user?.id ?? 'novo'}`}
                  mode={userSidePanel.mode}
                  user={userSidePanel.mode === 'edit' ? userSidePanel.user : null}
                  idGrupoRef={scope?.idGrupoRef}
                  codigosCliente={scope?.codigosCliente || []}
                  onClose={closeUserPanel}
                  onSave={handleSaveUserPortal}
                />
              </div>
            </aside>
          </div>,
          document.body
        )}
    </div>
  );
}
